import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DollarSign, Save, Lock, TrendingUp, Loader2, Calculator, ShieldAlert } from "lucide-react";

const money = (n) => (n === null || n === undefined || n === "" ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const F = [
  ["min_value", "Minimum Expected", "$"], ["expected_value", "Expected Settlement", "$"], ["max_value", "Maximum Expected", "$"],
  ["attorney_fee_pct", "Attorney Fee %", "%"], ["estimated_case_costs", "Estimated Case Costs", "$"], ["estimated_medical_liens", "Estimated Medical Liens", "$"],
  ["settlement_probability", "Settlement Probability", "%"], ["litigation_probability", "Litigation Probability", "%"], ["trial_probability", "Trial Probability", "%"],
];
const COST_FIELDS = ["estimated_case_costs", "estimated_medical_liens"];

export default function CfiPanel() {
  const { caseId } = useParams();
  const [cfi, setCfi] = useState(null);
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: cfg }] = await Promise.all([api.get(`/cases/${caseId}/cfi`), api.get("/cfi/config")]);
      setCfi(data); setStages(cfg.stages);
      setForm(F.reduce((a, [k]) => ({ ...a, [k]: data[k] ?? "" }), { financial_stage: data.financial_stage, expected_settlement_date: data.expected_settlement_date || "" }));
    } catch (e) { if (e?.response?.status === 403) setCfi({ forbidden: true }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  if (!cfi) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-cobalt animate-spin" /></div>;
  if (cfi.forbidden) return null;

  const canEdit = cfi.can_edit;
  const costOnly = cfi.cost_only;
  const editable = (k) => canEdit && (!costOnly || COST_FIELDS.includes(k));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      F.forEach(([k]) => { if (form[k] !== "" && form[k] !== null && editable(k)) payload[k] = Number(form[k]); });
      if (!costOnly) { payload.financial_stage = form.financial_stage; if (form.expected_settlement_date) payload.expected_settlement_date = form.expected_settlement_date; }
      const { data } = await api.put(`/cases/${caseId}/cfi`, payload);
      setCfi(data); toast.success("Financials saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-100 p-6" data-testid="cfi-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Case Financial Intelligence™</p>
          <h2 className="font-heading text-2xl font-extrabold text-white mt-1">Financial Readiness</h2>
        </div>
        <div className="text-right">
          <div className="text-3xl font-extrabold text-cyan-400 tabular-nums">{cfi.revenue_confidence ?? "—"}%</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">Revenue Confidence™</div>
        </div>
      </div>

      {costOnly && <div className="mt-3 flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2"><ShieldAlert className="w-4 h-4" /> Limited access: you may update costs & liens only. Revenue figures are hidden.</div>}

      {/* Auto-calculated summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        {[["Est. Attorney Fee", cfi.estimated_attorney_fee, "text-emerald-400"], ["Client Net Recovery", cfi.estimated_client_net_recovery, "text-slate-100"],
          ["Weighted Revenue", cfi.weighted_expected_revenue, "text-cyan-400"], ["Pipeline Contribution", cfi.pipeline_contribution, "text-cyan-400"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl bg-slate-900/60 border border-slate-800 p-3" data-testid={`cfi-calc-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide flex items-center gap-1"><Calculator className="w-3 h-3" /> {l}</div>
            <div className={`mt-1 text-xl font-extrabold tabular-nums ${c}`}>{money(v)}</div>
          </div>
        ))}
      </div>

      {/* Editable manual fields */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-5">
        {F.map(([k, label, unit]) => (
          <label key={k} className="block">
            <span className="text-[11px] text-slate-400">{label}</span>
            {(costOnly && !COST_FIELDS.includes(k)) ? (
              <div className="mt-1 flex items-center gap-1.5 text-slate-500 text-sm h-9"><Lock className="w-3.5 h-3.5" /> Hidden</div>
            ) : (
              <div className="mt-1 flex items-center rounded-lg bg-slate-900 border border-slate-700 focus-within:border-cyan-500">
                {unit === "$" && <span className="pl-2 text-slate-500">$</span>}
                <input type="number" value={form[k] ?? ""} disabled={!editable(k)} data-testid={`cfi-input-${k}`}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder="—"
                  className="w-full bg-transparent px-2 py-1.5 text-sm text-white outline-none disabled:opacity-50" />
                {unit === "%" && <span className="pr-2 text-slate-500">%</span>}
              </div>
            )}
          </label>
        ))}
        {!costOnly && (
          <>
            <label className="block">
              <span className="text-[11px] text-slate-400">Financial Stage</span>
              <select value={form.financial_stage} disabled={!canEdit} onChange={(e) => setForm({ ...form, financial_stage: e.target.value })} data-testid="cfi-stage"
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-sm text-white outline-none focus:border-cyan-500 disabled:opacity-50">
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-400">Expected Settlement Date (optional)</span>
              <input type="date" value={form.expected_settlement_date || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, expected_settlement_date: e.target.value })} data-testid="cfi-date"
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-cyan-500 disabled:opacity-50" />
            </label>
          </>
        )}
      </div>

      <div className="flex items-center justify-between mt-5 flex-wrap gap-2">
        <div className="text-[11px] text-slate-500">
          {cfi.last_review_date ? <>Last reviewed by {cfi.last_reviewed_by_name || "—"} · {(cfi.last_review_date || "").slice(0, 10)}</> : "Not yet reviewed"}
          {cfi.expected_revenue_date && <span className="ml-2 text-cyan-400/80">Expected revenue {cfi.expected_revenue_date}{cfi.expected_revenue_date_projected ? " (projected)" : ""}</span>}
        </div>
        {canEdit && (
          <button onClick={save} disabled={saving} data-testid="cfi-save" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Financials
          </button>
        )}
      </div>
      <p className="text-[10px] text-slate-600 mt-3">CFI™ never auto-generates values. All figures are attorney-entered; automatic fields are derived only from your inputs.</p>
    </div>
  );
}
